/**
 * TaskDetailPage — cleaner's primary work surface for a single task.
 *
 * Phase 1 scope:
 *   - Display room number + current_status + cycle_type
 *   - Render the checklist, items grouped by required/optional
 *   - Show status, started_at, completed_at
 *
 * Day 2 will add: photo upload, vision verification, transition button.
 * Status transitions never write `rooms.current_status` directly — they
 * always go through the room-transition Edge Function (CLAUDE.md ERROR 5).
 */

import { useState, type ReactNode } from 'react'
import { useParams }       from 'react-router'
import { useTranslation }  from 'react-i18next'
import { useTask, type ChecklistItemRow } from '@/hooks/useTask'
import { useCleaningAssistant }            from '@/hooks/useCleaningAssistant'
import { useTaskPhotos }                   from '@/hooks/useTaskPhotos'
import { useToggleChecklistItem }          from '@/hooks/useToggleChecklistItem'
import { PhotoUploader }                    from '@/components/photos/PhotoUploader'
import { TaskActionBar }                    from '@/components/tasks/TaskActionBar'
import { FeeRequestSection }                from '@/components/tasks/FeeRequestSection'
import { AbandonedBelongingsSection }       from '@/components/tasks/AbandonedBelongingsSection'
import { InspectionPanel }                  from '@/components/tasks/InspectionPanel'
import { CountersBadge }                    from '@/components/tasks/CountersBadge'
import { useRole, useUserId, useOrgId }    from '@/hooks/useAuth'
import { StartCleaningPanel }               from '@/components/tasks/StartCleaningPanel'
import { ReassignTaskPanel }                from '@/components/tasks/ReassignTaskPanel'
import { computeLocalBlock }                from '@/lib/tasks/preflight'
import { isAppError }                       from '@/lib/errors/AppError'

// Statuses where the cleaner has already submitted their counts and
// the recorded values become a read-only chip in the header. PROJECT_STATE
// task #74a — the counters tell the supervisor what the cleaner saw.
const SUBMITTED_STATUSES = new Set(['completed', 'inspection_pending', 'ready', 'rejected'])

export function TaskDetailPage(): ReactNode {
  const { t }      = useTranslation()
  const { taskId } = useParams<{ taskId: string }>()
  const role       = useRole()
  const userId     = useUserId()
  const orgId      = useOrgId()
  const { data,       isLoading, error }     = useTask(taskId)
  const { data: ai,   isLoading: aiLoading } = useCleaningAssistant(taskId)
  const { data: photos = [] }                = useTaskPhotos(taskId)

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return <ErrorPanel message={error instanceof Error ? error.message : t('task.failedToLoadTask')} />
  }

  if (!data) {
    return <ErrorPanel message={t('task.taskNotFound')} />
  }

  const { task, items } = data
  const required = items.filter((i) => i.is_required)
  const optional = items.filter((i) => !i.is_required)
  // Required-done count includes N/A items per #72 — treats them
  // identically to the server-side readiness guard so the displayed
  // ratio doesn't disagree with what blocks submission.
  const requiredDone = required.filter((i) => i.completed || i.is_not_applicable).length
  // Toggles are locked once the task is past the cleaner's hands.
  // The server enforces this too (RPC raises INVALID_STATE) — we
  // mirror it client-side so taps don't even fire and the UI shows
  // an obvious read-only signal.
  const checklistLocked = task.status !== 'pending' && task.status !== 'in_progress'

  return (
    <article style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <TaskLocationLine task={task} />
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('dashboard.room', { number: task.room?.room_number ?? '—' })}
        </h1>
        <p style={{ margin: '0.25rem 0', color: 'var(--brand-neutral-500)', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <StatusChip status={task.status} />
          <span>· {t('dashboard.clean', { type: task.cycle_type })}</span>
          {task.is_partial_unit && (
            <PartialUnitChip kitchenExcluded={task.kitchen_excluded} />
          )}
          {SUBMITTED_STATUSES.has(task.status) && (
            <CountersBadge
              trashBagCount={task.trash_bag_count}
              appliancesCleanedCount={task.appliances_cleaned_count}
            />
          )}
        </p>
        <p style={{ margin: 0, color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
          {t('task.startedLabel')} {task.started_at ? new Date(task.started_at).toLocaleString() : t('task.notYet')}
          {task.completed_at && <> · {t('task.completedLabel')} {new Date(task.completed_at).toLocaleString()}</>}
        </p>
        {task.is_partial_unit && task.partial_scope_description && (
          <p
            data-testid="partial-scope-description"
            style={{
              margin:       '0.5rem 0 0',
              padding:      '0.5rem 0.75rem',
              background:   'var(--brand-primary-subtle)',
              color:        'var(--brand-neutral-900)',
              borderRadius: 'var(--brand-radius-sm)',
              fontSize:     '0.85rem',
              borderLeft:   '3px solid var(--brand-primary)',
            }}
          >
            <strong>{t('task.partialScopeLabel', 'Partial scope:')}</strong>{' '}
            {task.partial_scope_description}
          </p>
        )}
      </header>

      {task.status === 'pending' && <StartCleaningPanel task={task} role={role} userId={userId} />}

      <ReassignTaskPanel task={task} role={role} orgId={orgId} />

      {ai?.blocking_issues && ai.blocking_issues.length > 0 && (
        <section
          aria-label="Blocking maintenance issues"
          style={{
            background:   'var(--brand-danger-subtle)',
            color:        'var(--brand-danger)',
            border:       '1px solid var(--brand-danger)',
            borderRadius: 'var(--brand-radius-md)',
            padding:      '0.75rem 1rem',
            fontSize:     '0.9rem',
          }}
        >
          <strong>{t('task.blockingIssues')}</strong> — {t('task.blockingDetail')}
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
            {ai.blocking_issues.map((i) => <li key={i.id}>{i.title} <small>({i.severity})</small></li>)}
          </ul>
        </section>
      )}

      {ai?.coaching_note && (
        <aside
          aria-label="AI coaching note"
          style={{
            background:   'var(--brand-primary-subtle)',
            color:        'var(--brand-neutral-900)',
            border:       '1px solid var(--brand-primary)',
            borderRadius: 'var(--brand-radius-md)',
            padding:      '0.75rem 1rem',
            fontSize:     '0.9rem',
          }}
        >
          <small style={{ color: 'var(--brand-primary)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t('task.tip')}
          </small>
          <p style={{ margin: '0.25rem 0 0' }}>{ai.coaching_note}</p>
        </aside>
      )}

      {ai?.coaching_status === 'unavailable' && (
        <aside
          data-testid="coaching-unavailable-hint"
          role="status"
          style={{
            background:   'var(--brand-warning-subtle, #fef9c3)',
            color:        'var(--brand-warning, #854d0e)',
            border:       '1px solid var(--brand-warning, #ca8a04)',
            borderRadius: 'var(--brand-radius-md)',
            padding:      '0.6rem 0.8rem',
            fontSize:     '0.85rem',
          }}
        >
          {t('task.coachingUnavailable')}
        </aside>
      )}

      {aiLoading && !ai && (
        <p style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem', margin: 0 }}>
          {t('task.loadingAI')}
        </p>
      )}

      <section
        aria-labelledby="checklist-required"
        style={{
          background:   'var(--brand-white)',
          border:       '1px solid var(--brand-neutral-300)',
          borderRadius: 'var(--brand-radius-lg)',
          padding:      '1rem',
        }}
      >
        <h2 id="checklist-required" style={{ margin: 0, fontSize: '1rem' }}>
          {t('task.required', { done: requiredDone, total: required.length })}
        </h2>
        {required.length === 0
          ? <EmptyHint label={t('task.noRequiredItems')} />
          : <ul style={{ margin: '0.5rem 0 0', padding: 0, listStyle: 'none' }}>
              {required.map((item) => <ChecklistItemRowView key={item.id} item={item} taskId={task.id} locked={checklistLocked} />)}
            </ul>}
      </section>

      {optional.length > 0 && (
        <section
          aria-labelledby="checklist-optional"
          style={{
            background:   'var(--brand-white)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-lg)',
            padding:      '1rem',
          }}
        >
          <h2 id="checklist-optional" style={{ margin: 0, fontSize: '1rem' }}>
            {t('task.optional')}
          </h2>
          <ul style={{ margin: '0.5rem 0 0', padding: 0, listStyle: 'none' }}>
            {optional.map((item) => <ChecklistItemRowView key={item.id} item={item} taskId={task.id} locked={checklistLocked} />)}
          </ul>
        </section>
      )}

      {ai?.photo_requirements && ai.photo_requirements.length > 0 && task.room && (
        <section
          aria-labelledby="photo-reqs"
          style={{
            background:   'var(--brand-white)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-lg)',
            padding:      '1rem',
          }}
        >
          <h2 id="photo-reqs" style={{ margin: 0, fontSize: '1rem' }}>{t('task.photos')}</h2>
          <ul style={{ margin: '0.5rem 0 0', padding: 0, listStyle: 'none' }}>
            {ai.photo_requirements.map((p) => (
              <PhotoUploader
                key={p.category}
                taskId={task.id}
                roomId={task.room!.id}
                category={p.category}
                isRequired={p.is_required}
                existingPhotos={photos}
              />
            ))}
          </ul>
        </section>
      )}

      <FeeRequestSection
        taskId={task.id}
        taskStatus={task.status}
        canRequest={task.assigned_to_user_id !== null && task.assigned_to_user_id === userId}
      />

      <AbandonedBelongingsSection
        taskId={task.id}
        taskStatus={task.status}
        canReport={task.assigned_to_user_id !== null && task.assigned_to_user_id === userId}
      />

      <InspectionPanel
        taskId={task.id}
        canWrite={role === 'supervisor' || role === 'contractor_admin' || role === 'super_admin'}
      />

      <TaskActionBar
        taskId={task.id}
        locallyBlocked={computeLocalBlock({
          requiredItems:   required,
          photoRequirements: ai?.photo_requirements ?? [],
          existingPhotos:  photos,
          taskStatus:      task.status,
        })}
      />
    </article>
  )
}

interface ChecklistItemRowViewProps {
  item:   ChecklistItemRow
  taskId: string
  locked: boolean
}

function ChecklistItemRowView({ item, taskId, locked }: ChecklistItemRowViewProps): ReactNode {
  const { t } = useTranslation()
  const toggle = useToggleChecklistItem()
  const [naFormOpen, setNaFormOpen] = useState(false)
  const [reasonDraft, setReasonDraft] = useState('')

  // An item is "settled" (struck through, no longer blocks readiness)
  // when either explicitly completed by the cleaner OR marked N/A
  // for this specific room (PROJECT_STATE task #72).
  const settled = item.completed || item.is_not_applicable
  const busy    = toggle.isPending && toggle.variables?.itemId === item.id
  const errMsg  = toggle.error && toggle.variables?.itemId === item.id && isAppError(toggle.error)
                    ? toggle.error.userMessage
                    : null

  function handleToggleCompleted(): void {
    if (locked || busy) return
    toggle.mutate({ taskId, itemId: item.id, completed: !item.completed })
  }

  function handleOpenNaForm(): void {
    if (locked || busy) return
    setReasonDraft(item.not_applicable_reason ?? '')
    setNaFormOpen(true)
  }

  function handleSaveNa(): void {
    const trimmed = reasonDraft.trim()
    toggle.mutate({
      taskId,
      itemId:              item.id,
      isNotApplicable:     true,
      notApplicableReason: trimmed.length > 0 ? trimmed : null,
    })
    setNaFormOpen(false)
  }

  function handleClearNa(): void {
    if (locked || busy) return
    toggle.mutate({ taskId, itemId: item.id, isNotApplicable: false })
  }

  return (
    <li
      data-testid="checklist-item"
      data-not-applicable={item.is_not_applicable ? 'true' : 'false'}
      data-locked={locked ? 'true' : 'false'}
      style={{
        display:        'flex',
        flexDirection:  'column',
        gap:            '0.35rem',
        padding:        '0.5rem 0',
        borderBottom:   '1px solid var(--brand-neutral-100)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <button
          type="button"
          onClick={handleToggleCompleted}
          disabled={locked || busy}
          aria-pressed={item.completed}
          aria-label={item.completed ? t('task.markIncomplete', 'Mark not done') : t('task.markComplete', 'Mark done')}
          data-testid="checklist-toggle"
          style={{
            display:        'inline-flex',
            alignItems:     'center',
            justifyContent: 'center',
            width:          '1.6rem',
            height:         '1.6rem',
            borderRadius:   'var(--brand-radius-full)',
            background:     settled ? 'var(--brand-accent)' : 'var(--brand-neutral-100)',
            color:          settled ? 'var(--brand-white)' : 'var(--brand-neutral-500)',
            border:         settled ? 'none' : '1px solid var(--brand-neutral-300)',
            fontSize:       '0.85rem',
            cursor:         locked ? 'default' : 'pointer',
            padding:        0,
            opacity:        busy ? 0.5 : 1,
          }}
        >
          {item.completed ? '✓' : item.is_not_applicable ? '–' : ''}
        </button>

        <span
          style={{
            flex:           1,
            color:          settled ? 'var(--brand-neutral-500)' : 'var(--brand-neutral-900)',
            textDecoration: settled ? 'line-through' : 'none',
            cursor:         locked ? 'default' : 'pointer',
          }}
          onClick={handleToggleCompleted}
        >
          {item.label}
        </span>

        {item.is_not_applicable && (
          <span
            data-testid="na-badge"
            title={item.not_applicable_reason ?? undefined}
            style={{
              fontSize:     '0.65rem',
              padding:      '0.1rem 0.4rem',
              borderRadius: 'var(--brand-radius-full)',
              background:   'var(--brand-neutral-100)',
              color:        'var(--brand-neutral-700)',
              fontWeight:   600,
            }}
          >
            N/A
          </span>
        )}

        {!locked && (
          item.is_not_applicable ? (
            <button
              type="button"
              onClick={handleClearNa}
              disabled={busy}
              data-testid="checklist-clear-na"
              style={{
                background: 'transparent',
                border:     'none',
                color:      'var(--brand-primary)',
                fontSize:   '0.75rem',
                cursor:     'pointer',
                padding:    '0.1rem 0.3rem',
              }}
            >
              {t('task.clearNa', 'Clear N/A')}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleOpenNaForm}
              disabled={busy}
              data-testid="checklist-mark-na"
              style={{
                background: 'transparent',
                border:     'none',
                color:      'var(--brand-neutral-500)',
                fontSize:   '0.75rem',
                cursor:     'pointer',
                padding:    '0.1rem 0.3rem',
              }}
            >
              {t('task.markNa', 'N/A')}
            </button>
          )
        )}
      </div>

      {naFormOpen && !locked && (
        <div
          data-testid="checklist-na-form"
          style={{
            display:        'flex',
            flexDirection:  'column',
            gap:            '0.4rem',
            marginLeft:     '2.2rem',
            padding:        '0.5rem',
            background:     'var(--brand-neutral-50, var(--brand-neutral-100))',
            borderRadius:   'var(--brand-radius-sm)',
          }}
        >
          <label htmlFor={`na-reason-${item.id}`} style={{ fontSize: '0.75rem', color: 'var(--brand-neutral-500)' }}>
            {t('task.naReasonLabel', 'Reason (optional, ≤200 chars)')}
          </label>
          <textarea
            id={`na-reason-${item.id}`}
            data-testid="checklist-na-reason"
            value={reasonDraft}
            onChange={(e) => setReasonDraft(e.target.value.slice(0, 200))}
            rows={2}
            maxLength={200}
            style={{
              width:        '100%',
              fontSize:     '0.85rem',
              padding:      '0.3rem',
              borderRadius: 'var(--brand-radius-sm)',
              border:       '1px solid var(--brand-neutral-300)',
              resize:       'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              type="button"
              onClick={handleSaveNa}
              data-testid="checklist-na-save"
              style={{
                background:    'var(--brand-primary)',
                color:         'var(--brand-white)',
                border:        'none',
                borderRadius:  'var(--brand-radius-sm)',
                fontSize:      '0.8rem',
                padding:       '0.3rem 0.7rem',
                cursor:        'pointer',
              }}
            >
              {t('task.naSave', 'Mark N/A')}
            </button>
            <button
              type="button"
              onClick={() => setNaFormOpen(false)}
              data-testid="checklist-na-cancel"
              style={{
                background:   'transparent',
                color:        'var(--brand-neutral-500)',
                border:       '1px solid var(--brand-neutral-300)',
                borderRadius: 'var(--brand-radius-sm)',
                fontSize:     '0.8rem',
                padding:      '0.3rem 0.7rem',
                cursor:       'pointer',
              }}
            >
              {t('task.naCancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {errMsg && (
        <p
          role="alert"
          data-testid="checklist-error"
          style={{
            margin:     '0 0 0 2.2rem',
            color:      'var(--brand-danger)',
            fontSize:   '0.75rem',
          }}
        >
          {errMsg}
        </p>
      )}
    </li>
  )
}

function TaskLocationLine({ task }: { task: { room: { campus?: { name: string } | null; building?: { name: string } | null; floor?: { label: string } | null } | null } }): ReactNode {
  const parts: string[] = []
  if (task.room?.campus?.name)   parts.push(task.room.campus.name)
  if (task.room?.building?.name) parts.push(task.room.building.name)
  if (task.room?.floor?.label)   parts.push(task.room.floor.label)
  if (parts.length === 0) return null
  return (
    <div
      data-testid="task-location"
      style={{
        marginBottom: '0.25rem',
        color:        'var(--brand-neutral-500)',
        fontSize:     '0.8rem',
        letterSpacing:'0.02em',
      }}
    >
      {parts.join(' · ')}
    </div>
  )
}

function PartialUnitChip({ kitchenExcluded }: { kitchenExcluded: boolean }): ReactNode {
  // Renders alongside StatusChip on tasks where is_partial_unit=true.
  // Sub-chip variant when kitchen_excluded is also true — the contract
  // doc's most common partial-unit shape ("all rooms minus kitchen").
  return (
    <span
      data-testid="partial-unit-chip"
      data-kitchen-excluded={kitchenExcluded ? 'true' : 'false'}
      style={{
        display:       'inline-block',
        padding:       '0.1rem 0.5rem',
        borderRadius:  'var(--brand-radius-full)',
        background:    'var(--brand-accent-subtle, var(--brand-neutral-100))',
        color:         'var(--brand-accent, var(--brand-neutral-900))',
        border:        '1px solid var(--brand-accent, var(--brand-neutral-300))',
        fontSize:      '0.75rem',
        fontWeight:    600,
      }}
    >
      Partial unit{kitchenExcluded ? ' · no kitchen' : ''}
    </span>
  )
}

function StatusChip({ status }: { status: string }): ReactNode {
  return (
    <span
      style={{
        display:        'inline-block',
        padding:        '0.1rem 0.5rem',
        borderRadius:   'var(--brand-radius-full)',
        background:     'var(--brand-primary-subtle)',
        color:          'var(--brand-primary)',
        fontSize:       '0.75rem',
        fontWeight:     600,
        textTransform:  'capitalize',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function Loading(): ReactNode {
  const { t } = useTranslation()
  return (
    <div role="status" style={{ color: 'var(--brand-neutral-500)' }}>
      {t('task.loadingTask')}
    </div>
  )
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
        fontSize:     '0.9rem',
      }}
    >
      {message}
    </div>
  )
}

function EmptyHint({ label }: { label: string }): ReactNode {
  return (
    <p style={{ color: 'var(--brand-neutral-500)', margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
      {label}
    </p>
  )
}

